import { useState, useEffect } from "react";
import { FadeIn } from "@/components/common/FadeIn";
import { SEO } from "@/components/common/SEO";

const GALLERY_IMAGE = "https://customer-assets.emergentagent.com/job_fine-art-frame/artifacts/ykl7xb3x_IMG_2603.JPG";
const HERO_GALLERY = "https://customer-assets.emergentagent.com/job_fine-art-frame/artifacts/j703vr6w_IMG_2606.JPG";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_fine-art-frame/artifacts/zcv1xf8i_IMG_2606.JPG";

const FOUNDERS_PHOTO = "https://customer-assets.emergentagent.com/job_ffc8d3b8-f252-461d-b818-6d0d3720c5ae/artifacts/nke7tj3p_Screenshot%202026-04-16%20at%209.21.33%E2%80%AFPM.png";

const REVEAL_DELAY = 500;

export default function About() {
  const [animateReady, setAnimateReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimateReady(true), REVEAL_DELAY);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div data-testid="about-page" className="pt-20">
      <SEO
        title="About Rain Boy Fine Art | Tulsa Art Gallery"
        description="Learn about Rain Boy Fine Art, a contemporary art gallery in Tulsa, OK founded by Mary and Andrew Lauro. Dedicated to championing emerging and established artists."
        path="/about"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]}
        schemas={[{ "@context": "https://schema.org", "@type": "AboutPage", name: "About Rain Boy Fine Art", url: "https://rainboyfineart.com/about", mainEntity: { "@type": "ArtGallery", "@id": "https://rainboyfineart.com/#gallery" } }]}
      />
      {/* Header */}
      <section className="py-16 md:py-24 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeIn ready={animateReady}>
            <p className="overline mb-4">About Us</p>
            <h1 className="section-heading font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0A1128] mb-14">
              Our Story
            </h1>
          </FadeIn>

          {/* Story Content */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 lg:gap-20 items-start">
            <FadeIn ready={animateReady}>
              <div className="relative px-6 py-5">
                <div className="absolute -top-3 -left-3 w-[22px] h-[22px] border-t border-l border-[#CBA365]" />
                <div className="absolute bottom-0 right-0 w-[22px] h-[22px] border-b border-r border-[#CBA365]" />
                <div className="text-[#4A5568] leading-relaxed space-y-6 text-sm sm:text-base">
                <p>
                  Rain Boy Fine Art was founded in 2020 with a simple but ambitious mission: to bring
                  world-class contemporary art to the heart of Tulsa, Oklahoma. What began as a small
                  pop-up gallery in the Tulsa Arts District has grown into one of the region's most
                  respected contemporary art spaces.
                </p>
                <p>
                  We represent a carefully curated roster of emerging and established artists whose work
                  spans painting, sculpture, photography, mixed media, and installation. Every exhibition
                  we present is designed to challenge, inspire, and connect — creating conversations between
                  artists, collectors, and the broader community.
                </p>
                <p>
                  Our gallery occupies a beautifully renovated space on Boston Avenue, featuring 3,000
                  square feet of exhibition space with soaring ceilings, natural light, and a design that
                  puts the art first. We host regular exhibitions, artist talks, workshops, and community
                  events that make art accessible to everyone.
                </p>
              </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.15} ready={animateReady}>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full bg-[#0A1128]" />
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img src={GALLERY_IMAGE} alt="Rain Boy Fine Art gallery interior in Tulsa, OK" className="w-full h-full object-cover" />
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="py-20 md:py-28 px-6 lg:px-12 bg-[#F8F9FA]" data-testid="founders-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch">
            <FadeIn ready={animateReady}>
              <div className="relative h-full">
                <div className="relative aspect-[3/4] lg:aspect-auto lg:h-full overflow-hidden">
                  <img
                    src={FOUNDERS_PHOTO}
                    alt="Mary and Andrew Lauro, co-founders of Rain Boy Fine Art"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </FadeIn>
            <FadeIn ready={animateReady}>
              <div className="bg-[#0A1128] p-8 md:p-10 lg:p-12 flex flex-col justify-center h-full">
                <p className="overline mb-4">Co-Founders</p>
                <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-white mb-2" data-testid="founders-names">
                  Mary & Andrew Lauro
                </h2>
                <div className="w-10 h-[1px] bg-[#CBA365] my-6" />
                <div className="text-white/70 leading-relaxed space-y-5 text-sm sm:text-base" data-testid="founders-bio">
                  <p>
                    Mary and Andrew Lauro have always shared a deep love for art and for their hometown. After
                    spending years immersed in the contemporary art scenes of New York and Chicago — Mary as a
                    curator and Andrew as an arts administrator — they returned to Tulsa with a shared vision:
                    to create a gallery that could stand alongside the best in the country while remaining
                    rooted in the community they call home.
                  </p>
                  <p>
                    Together they founded Rain Boy Fine Art in 2020, transforming a historic space on Boston
                    Avenue into a destination for collectors, artists, and anyone curious about contemporary
                    American art. Their complementary perspectives — Mary's eye for emerging talent and
                    Andrew's passion for building lasting relationships with artists and patrons — have shaped
                    the gallery's identity from its earliest days.
                  </p>
                  <p>
                    When they're not at the gallery, the Lauros are active supporters of arts education in
                    Tulsa, working with local schools and nonprofit organizations to make art accessible to
                    the next generation.
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
}
